// Auto-lock (Implementation Plan v02 §9 app-lock row; I-1a Batch 4, IP2-29 (5)).
//
// Locks after 5 minutes without a touch or key press, and when the app comes
// back after more than 30 s in the background. A timer also runs while hidden,
// in case the browser lets it fire; the check on return is the one that counts
// (background timers may be throttled). Locking = the shell closes the store
// (drops the key + decrypted state) and resets the screens.
//
// The clock, timers and event targets are parameters so tests use fake timers.

export const IDLE_LOCK_MS = 5 * 60 * 1000
export const BACKGROUND_LOCK_MS = 30 * 1000

const ACTIVITY_EVENTS: readonly string[] = ['pointerdown', 'keydown', 'wheel', 'touchstart']

export interface AutoLockEnv {
  readonly now: () => number
  readonly setTimer: (run: () => void, ms: number) => unknown
  readonly clearTimer: (timer: unknown) => void
  /** Where touches and key presses arrive (the window). */
  readonly activity: EventTarget
  /** Fires `visibilitychange` and says whether the app is visible (the document). */
  readonly page: EventTarget & { readonly visibilityState: DocumentVisibilityState }
}

function browserEnv(): AutoLockEnv {
  return {
    now: () => Date.now(),
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (timer) => window.clearTimeout(timer as number),
    activity: window,
    page: document,
  }
}

/** Start watching; `onLock` runs at most once. Returns a stop function. */
export function startAutoLock(onLock: () => void, env: AutoLockEnv = browserEnv()): () => void {
  let stopped = false
  let idleTimer: unknown = undefined
  let backgroundTimer: unknown = undefined
  let hiddenAt: number | null = null

  const stop = (): void => {
    stopped = true
    env.clearTimer(idleTimer)
    env.clearTimer(backgroundTimer)
    for (const type of ACTIVITY_EVENTS) {
      env.activity.removeEventListener(type, onActivity)
    }
    env.page.removeEventListener('visibilitychange', onVisibility)
  }

  const fire = (): void => {
    if (!stopped) {
      stop()
      onLock()
    }
  }

  const armIdle = (): void => {
    env.clearTimer(idleTimer)
    idleTimer = env.setTimer(fire, IDLE_LOCK_MS)
  }

  function onActivity(): void {
    if (hiddenAt === null) {
      armIdle()
    }
  }

  function onVisibility(): void {
    if (env.page.visibilityState === 'hidden') {
      if (hiddenAt === null) {
        hiddenAt = env.now()
        env.clearTimer(idleTimer)
        backgroundTimer = env.setTimer(fire, BACKGROUND_LOCK_MS)
      }
      return
    }
    env.clearTimer(backgroundTimer)
    const away = hiddenAt === null ? 0 : env.now() - hiddenAt
    hiddenAt = null
    if (away >= BACKGROUND_LOCK_MS) {
      fire()
    } else {
      armIdle()
    }
  }

  for (const type of ACTIVITY_EVENTS) {
    env.activity.addEventListener(type, onActivity, { passive: true })
  }
  env.page.addEventListener('visibilitychange', onVisibility)
  if (env.page.visibilityState === 'hidden') {
    onVisibility()
  } else {
    armIdle()
  }
  return stop
}
